/**
 * Cookie Consent Management for Secret Book Price
 *
 * Handles GDPR/CCPA compliant cookie consent state management.
 * Integrates with Bronze tier activation - accepting functional cookies
 * enables Bronze tier features.
 *
 * Categories:
 * - Essential: Always on, required for site functionality
 * - Functional: Device token for Bronze tier, saved preferences
 * - Analytics: Google Analytics tracking
 *
 * @module web/lib/cookieConsent
 */

import { getAuthState } from "./auth";

// =============================================================================
// Types & Interfaces
// =============================================================================

/**
 * Cookie consent categories
 */
export type ConsentCategory = "essential" | "functional" | "analytics";

/**
 * Stored consent state
 */
export interface ConsentState {
  /** Essential cookies - always true */
  essential: boolean;
  /** Functional cookies - enables Bronze tier */
  functional: boolean;
  /** Analytics cookies - Google Analytics */
  analytics: boolean;
  /** Unix timestamp when consent was given */
  timestamp: number;
  /** Consent version for re-prompting on policy changes */
  version: string;
  /** True if consent was inferred from existing tier status */
  implicit?: boolean;
}

// =============================================================================
// Constants
// =============================================================================

const STORAGE_KEY = "sbp_cookie_consent";
const CONSENT_VERSION = "1.0";

// Consent expires after 6 months (CNIL/ICO recommendation)
const CONSENT_EXPIRY_MS = 180 * 24 * 60 * 60 * 1000; // 180 days

// =============================================================================
// Core Functions
// =============================================================================

/**
 * Get the current consent state from localStorage
 */
export function getConsentState(): ConsentState | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const state: ConsentState = JSON.parse(stored);

    // Validate structure
    if (
      typeof state.essential !== "boolean" ||
      typeof state.functional !== "boolean" ||
      typeof state.analytics !== "boolean" ||
      typeof state.timestamp !== "number" ||
      typeof state.version !== "string"
    ) {
      console.warn("Invalid consent state structure, clearing");
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    // Check if consent has expired (6 months)
    if (Date.now() - state.timestamp > CONSENT_EXPIRY_MS) {
      console.info("Consent has expired, will re-prompt");
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    // Check if consent version has changed (requires re-consent)
    if (state.version !== CONSENT_VERSION) {
      console.info("Consent version changed, will re-prompt");
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return state;
  } catch (error) {
    console.error("Failed to read consent state:", error);
    return null;
  }
}

/**
 * Save consent state to localStorage
 */
export function setConsentState(
  state: Omit<ConsentState, "timestamp" | "version"> & {
    timestamp?: number;
    version?: string;
    implicit?: boolean;
  },
): void {
  try {
    const fullState: ConsentState = {
      essential: true, // Always true
      functional: state.functional,
      analytics: state.analytics,
      timestamp: state.timestamp ?? Date.now(),
      version: state.version ?? CONSENT_VERSION,
      implicit: state.implicit,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(fullState));

    // Update Google Analytics consent if available
    updateGoogleAnalyticsConsent(fullState.analytics);
  } catch (error) {
    console.error("Failed to save consent state:", error);
  }
}

/**
 * Check if user has given any consent (accepted or rejected)
 */
export function hasConsented(): boolean {
  return getConsentState() !== null;
}

/**
 * Check if user has consented to a specific category
 */
export function hasConsentedTo(category: ConsentCategory): boolean {
  const state = getConsentState();
  if (!state) {
    // Essential is always allowed even without explicit consent
    return category === "essential";
  }
  return state[category] === true;
}

/**
 * Revoke all consent (user can do this via settings)
 */
export function revokeConsent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    updateGoogleAnalyticsConsent(false);
  } catch (error) {
    console.error("Failed to revoke consent:", error);
  }
}

// =============================================================================
// Popup Display Logic
// =============================================================================

/**
 * Check if the consent popup should be shown
 *
 * Returns false if:
 * - User has already given consent (accepted or rejected)
 * - User is already Bronze/Silver/Gold tier (implicit consent)
 */
export function shouldShowConsentPopup(): boolean {
  // Already has explicit consent record
  if (hasConsented()) {
    return false;
  }

  // Check if user already has a tier (Bronze, Silver, or Gold)
  // If they do, they've implicitly accepted functional cookies
  // (you can't have a tier without the device token being stored)
  try {
    const authState = getAuthState();
    if (authState.isAuthenticated && authState.tier) {
      // Auto-record implicit consent for existing tier users
      setConsentState({
        essential: true,
        functional: true,
        analytics: true, // Assume yes for existing users
        implicit: true,
      });
      console.info(
        `Implicit consent recorded for existing ${authState.tier} tier user`,
      );
      return false;
    }
  } catch (error) {
    // If auth check fails, show the popup to be safe
    console.warn("Failed to check auth state:", error);
  }

  return true;
}

// =============================================================================
// Google Analytics Integration
// =============================================================================

/**
 * Update Google Analytics consent state
 *
 * Uses Google's Consent Mode v2 API if available.
 * This should be called after consent state changes.
 */
export function updateGoogleAnalyticsConsent(granted: boolean): void {
  try {
    // Check if gtag is available (Google Analytics)
    if (typeof window !== "undefined" && "gtag" in window) {
      const gtag = (window as unknown as { gtag: Gtag.Gtag }).gtag;
      gtag("consent", "update", {
        analytics_storage: granted ? "granted" : "denied",
      });
      console.debug(
        `Google Analytics consent ${granted ? "granted" : "denied"}`,
      );
    }
  } catch (error) {
    console.warn("Failed to update Google Analytics consent:", error);
  }
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get a human-readable summary of current consent
 */
export function getConsentSummary(): string {
  const state = getConsentState();
  if (!state) {
    return "No consent given";
  }

  const categories: string[] = ["Essential (always on)"];
  if (state.functional) categories.push("Functional");
  if (state.analytics) categories.push("Analytics");

  const date = new Date(state.timestamp).toLocaleDateString();
  const implicit = state.implicit ? " (implicit)" : "";

  return `Consented to: ${categories.join(", ")} on ${date}${implicit}`;
}

/**
 * Check if consent is about to expire (within 30 days)
 */
export function isConsentExpiringSoon(): boolean {
  const state = getConsentState();
  if (!state) {
    return false;
  }

  const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
  const expiresAt = state.timestamp + CONSENT_EXPIRY_MS;

  return expiresAt - Date.now() < thirtyDaysMs;
}

// =============================================================================
// Type Declarations for Google Analytics
// =============================================================================

// Extend Window interface for gtag
declare global {
  namespace Gtag {
    interface Gtag {
      (
        command: "consent",
        action: "update" | "default",
        params: {
          analytics_storage?: "granted" | "denied";
          ad_storage?: "granted" | "denied";
        },
      ): void;
    }
  }
}
